import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import { Box } from '@mui/material';
import { looksLikeHtml } from 'common/utils/markdown';
import { proseSx } from 'components/shared/content/proseStyles';
import PdfViewModal from 'components/shared/modals/PdfViewModal';

// Propiedades CSS seguras permitidas en `style` (alineación de TipTap + énfasis básico).
// Se descarta todo lo demás (color/background/position/url…) para cerrar exfiltración por CSS.
const SAFE_STYLE_PROPS = new Set([
  'text-align',
  'font-weight',
  'font-style',
  'text-decoration',
  'text-decoration-line',
  // Ancho de imágenes del editor (ResizableImage persiste style="width: NN%").
  'width'
]);

const sanitizeInlineStyle = (style) =>
  style
    .split(';')
    .map((decl) => decl.trim())
    .filter(Boolean)
    .filter((decl) => {
      const idx = decl.indexOf(':');
      if (idx < 0) return false;
      const prop = decl.slice(0, idx).trim().toLowerCase();
      const value = decl.slice(idx + 1).toLowerCase();
      return SAFE_STYLE_PROPS.has(prop) && !/url\(|expression|javascript:|@import/.test(value);
    })
    .join('; ');

// Solo iframes hacia estos reproductores oficiales sobreviven la sanitización.
const EMBED_IFRAME_RE = /^https:\/\/(www\.youtube-nocookie\.com\/embed\/|www\.youtube\.com\/embed\/|player\.vimeo\.com\/video\/|open\.spotify\.com\/embed\/)/;

// Hook GLOBAL (una sola vez): filtra `style` a props seguras y fuerza rel en enlaces target=_blank.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.hasAttribute && node.hasAttribute('style')) {
    const safe = sanitizeInlineStyle(node.getAttribute('style'));
    if (safe) node.setAttribute('style', safe);
    else node.removeAttribute('style');
  }
  // Enlaces: todo lo que apunte fuera se abre en pestaña nueva. Sin esto, pulsar un link dentro
  // del modal de un programa navegaba en la misma pestaña y sacaba al usuario de la aplicación,
  // perdiendo lo que estuviera viendo.
  if (node.tagName === 'A' && node.getAttribute) {
    const href = node.getAttribute('href') || '';
    if (/^https?:/i.test(href)) {
      node.setAttribute('target', '_blank');
    }
    if (node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer');
    }
  }
  // Multimedia del editor: asegurar reproductor visible (BlockNote no siempre
  // serializa `controls`) sin permitir autoplay.
  if (node.tagName === 'VIDEO' || node.tagName === 'AUDIO') {
    node.setAttribute('controls', '');
    node.setAttribute('preload', 'metadata');
    node.removeAttribute('autoplay');
  }
  // iframes: SOLO reproductores oficiales whitelisteados (YouTube/Vimeo); el resto se elimina.
  if (node.tagName === 'IFRAME') {
    const src = node.getAttribute('src') || '';
    if (!EMBED_IFRAME_RE.test(src)) {
      node.remove();
    } else {
      node.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-presentation allow-popups');
      node.setAttribute('loading', 'lazy');
    }
  }
});

// Convierte URLs de YouTube/YouTube Music/Vimeo/Spotify a su URL de EMBED oficial.
// Devuelve { url, kind:'video'|'spotify' } o null si no es embebible.
const toEmbedUrl = (raw) => {
  try {
    const url = new URL(raw);
    const host = url.hostname.replace(/^www\./, '');
    // YouTube + YouTube Music (music.youtube.com usa el mismo reproductor embed).
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com' || host === 'youtube-nocookie.com') {
      const id = url.searchParams.get('v') || url.pathname.match(/\/(?:embed|shorts|live)\/([\w-]{6,})/)?.[1];
      return id ? { url: `https://www.youtube-nocookie.com/embed/${id}`, kind: 'video' } : null;
    }
    if (host === 'youtu.be') {
      const id = url.pathname.slice(1).split('/')[0];
      return id ? { url: `https://www.youtube-nocookie.com/embed/${id}`, kind: 'video' } : null;
    }
    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      const id = url.pathname.match(/(\d{6,})/)?.[1];
      return id ? { url: `https://player.vimeo.com/video/${id}`, kind: 'video' } : null;
    }
    // Spotify (track/album/playlist/episode/show/artist) → reproductor embed oficial.
    if (host === 'open.spotify.com') {
      const m = url.pathname.match(/\/(track|album|playlist|episode|show|artist)\/([A-Za-z0-9]+)/);
      return m ? { url: `https://open.spotify.com/embed/${m[1]}/${m[2]}`, kind: 'spotify' } : null;
    }
    return null;
  } catch {
    return null;
  }
};

// Pre-transformación: la "lista plegable" (toggle) de BlockNote se exporta como un bloque con
// `data-content-type="toggleListItem"` + un `.bn-block-group` hermano con los hijos. En el render
// estático no hay JS, así que se convierte a `<details open><summary>…</summary>…</details>`
// NATIVO, que se pliega/despliega solo al hacer click (sin JS). Se corre ANTES del sanitize porque
// el sanitizer descarta los `data-*` que marcan el toggle.
const convertToggles = (html) => {
  if (!html || !html.includes('toggleListItem')) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('[data-content-type="toggleListItem"]').forEach((content) => {
    const container = content.closest('[data-node-type="blockContainer"]') || content.parentElement;
    if (!container) return;
    const childGroup = container.querySelector(':scope > .bn-block-group');
    const details = doc.createElement('details');
    details.setAttribute('open', '');
    details.className = 'otx-toggle';
    const summary = doc.createElement('summary');
    summary.innerHTML = content.innerHTML || content.textContent || '';
    details.appendChild(summary);
    if (childGroup) details.appendChild(childGroup);
    container.replaceWith(details);
  });
  return doc.body.innerHTML;
};

// Extensiones que abrimos en el visor embebido en vez de mandar a otra pestaña.
const PDF_RE = /\.pdf(?:[?#]|$)/i;
// Una imagen adjuntada con el bloque "archivo" del editor sigue siendo una imagen: hay que
// PINTARLA, no ofrecerla como descarga. El editor permite subirla por cualquiera de los dos
// bloques y el autor no distingue entre ellos, así que lo decide la extensión.
const IMAGE_RE = /\.(?:jpe?g|png|gif|webp|avif|svg)(?:[?#]|$)/i;

export const isPdfUrl = (url = '') => PDF_RE.test(url);
export const isImageUrl = (url = '') => IMAGE_RE.test(url);

// Pre-transformación: rescata los archivos adjuntos del editor (PDF, docs...).
//
// BlockNote, en modo `lossless` (blocksToFullHTML), serializa el bloque `file` SIN ancla: deja la
// URL solo en un atributo `data-url` sobre un div. Y el sanitizer, que trabaja con lista blanca de
// atributos, borra ese `data-url`. Resultado: el bloque conservaba el `cursor: pointer` de la hoja
// de estilos de BlockNote, así que parecía pulsable, pero ya no quedaba ni rastro de la URL y no
// abría nada. Por eso hay que reconstruir el <a> ANTES de sanitizar, igual que con los toggles.
//
// Funciona también sobre el contenido ya guardado, que es la mayoría: no hay que reeditar nada.
const convertFileBlocks = (html) => {
  if (!html || !html.includes('data-url')) return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');

  // OJO con el selector: `data-file-block` NO marca solo los archivos. BlockNote se lo pone a
  // TODOS los bloques que aceptan subida (imagen, video, audio y archivo), porque comparten la
  // misma base. Seleccionarlos a todos convertía el video de YouTube en un adjunto con clip y
  // desaparecía el reproductor. Cada tipo se reconstruye con el elemento que le corresponde.
  doc.querySelectorAll('[data-content-type="file"], [data-file-block]').forEach((node) => {
    const url = node.getAttribute('data-url');
    if (!url) return;

    const contentType = node.getAttribute('data-content-type');
    const container = node.closest('[data-node-type="blockContainer"]') || node;

    if (contentType === 'image') {
      const img = doc.createElement('img');
      img.setAttribute('src', url);
      img.setAttribute('alt', node.getAttribute('data-name') || '');
      img.setAttribute('loading', 'lazy');
      const align = node.getAttribute('data-text-alignment');
      if (align) img.setAttribute('data-text-alignment', align);
      container.replaceWith(img);
      return;
    }

    // Video y audio salen como elementos de medios reales. Si la URL es de YouTube, Vimeo o
    // Spotify, convertEmbeds (que corre justo después) los cambia por su reproductor oficial.
    if (contentType === 'video' || contentType === 'audio') {
      const media = doc.createElement(contentType);
      media.setAttribute('src', url);
      media.setAttribute('controls', '');
      media.setAttribute('preload', 'metadata');
      container.replaceWith(media);
      return;
    }

    // El nombre puede venir en el atributo o en el <p> que pinta BlockNote.
    const name =
      node.getAttribute('data-name') ||
      node.querySelector('.bn-file-name')?.textContent?.trim() ||
      url.split('/').pop() ||
      'Archivo';

    // Imagen subida por el bloque de archivo: se pinta a ancho de contenido en vez de quedar
    // como un adjunto con clip que el lector tiene que abrir aparte para ver de qué se trata.
    if (isImageUrl(url)) {
      const img = doc.createElement('img');
      img.setAttribute('src', url);
      img.setAttribute('alt', name);
      img.setAttribute('loading', 'lazy');
      container.replaceWith(img);
      return;
    }

    const link = doc.createElement('a');
    link.className = 'otx-file';
    link.setAttribute('href', url);
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');

    const icon = doc.createElement('span');
    icon.className = 'otx-file-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = isPdfUrl(url) ? '📄' : '📎';

    const label = doc.createElement('span');
    label.className = 'otx-file-name';
    label.textContent = name;

    link.appendChild(icon);
    link.appendChild(label);

    container.replaceWith(link);
  });

  return doc.body.innerHTML;
};

// Pre-transformación: convierte a reproductor (<iframe> oficial) los medios embebibles, ya sea
// que BlockNote los guarde como <video|audio src=…> (bloque de media) o como <a href=…> (link de
// Spotify/YouTube/YT Music pegado como texto). Se corre ANTES de sanitizar. Spotify lleva altura
// propia (no es 16:9).
const convertEmbeds = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  // Construye el reproductor + un link de respaldo "Abrir en …" (para oír completo en la app
  // original: Spotify da muestra de 30s y YouTube pide login en contenido explícito a quien no
  // tiene sesión). `originalUrl` es el link tal cual lo puso el usuario.
  const buildEmbed = (embed, source, originalUrl) => {
    const wrap = doc.createElement('div');
    wrap.className = 'otx-embed';

    const iframe = doc.createElement('iframe');
    iframe.setAttribute('src', embed.url);
    if (embed.kind === 'spotify') {
      iframe.setAttribute('data-embed', 'spotify');
      iframe.setAttribute('allow', 'encrypted-media; clipboard-write; fullscreen; picture-in-picture');
      iframe.setAttribute('height', '152');
    } else {
      iframe.setAttribute('allow', 'accelerometer; encrypted-media; gyroscope; picture-in-picture');
      iframe.setAttribute('allowfullscreen', '');
    }
    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    const align = source.getAttribute('data-text-alignment');
    if (align) iframe.setAttribute('data-text-alignment', align);
    wrap.appendChild(iframe);

    if (originalUrl) {
      const link = doc.createElement('a');
      link.className = 'otx-embed-link';
      link.setAttribute('href', originalUrl);
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
      link.textContent = embed.kind === 'spotify' ? '↗ Abrir en Spotify' : '↗ Ver en YouTube';
      wrap.appendChild(link);
    }
    return wrap;
  };

  // Bloques de media (<video|audio|embed|iframe src=…>).
  doc.querySelectorAll('video[src], audio[src], embed[src], iframe[src]').forEach((node) => {
    const src = node.getAttribute('src') || '';
    const embed = toEmbedUrl(src);
    if (embed) node.replaceWith(buildEmbed(embed, node, src));
  });

  // Links pegados (<a href=…>) que apuntan a un medio embebible → reproductor.
  doc.querySelectorAll('a[href]').forEach((node) => {
    const href = node.getAttribute('href') || '';
    const embed = toEmbedUrl(href);
    if (embed) node.replaceWith(buildEmbed(embed, node, href));
  });

  return doc.body.innerHTML;
};

// Sanitiza el HTML del editor (TipTap) o legado (CKEditor) antes de pintarlo. Bloquea
// scripts/eventos/CSS peligroso (anti-XSS / anti-exfiltración).
// El ORDEN importa: primero se reconstruyen los bloques de medios a elementos reales
// (convertFileBlocks) y solo después se buscan reproductores (convertEmbeds). Al revés, un video de
// YouTube guardado como bloque todavía es un <div> con la URL en un atributo, así que convertEmbeds
// no lo reconocía y el reproductor no aparecía.
const sanitize = (html) =>
  DOMPurify.sanitize(convertEmbeds(convertToggles(convertFileBlocks(html))), {
    // iframe SOLO para los embeds whitelisteados (el hook elimina cualquier otro src).
    ADD_TAGS: ['iframe'],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'style', 'target', 'rel', 'colspan', 'rowspan', 'data-align', 'data-text-alignment', 'data-embed', 'open',
      // Multimedia (video/audio del editor BlockNote) + embeds.
      'controls', 'poster', 'preload', 'playsinline', 'muted', 'loop', 'type', 'width', 'height',
      'allow', 'allowfullscreen', 'frameborder', 'referrerpolicy'
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i
  });

/**
 * Renderiza contenido de noticias / T&C. Render DUAL: si el valor parece HTML (TipTap o CKEditor
 * legado) lo sanitiza y pinta como HTML; si no, lo interpreta como Markdown (react-markdown + GFM).
 */
function ContentRenderer({ value = '', sx = {} }) {
  const mergedSx = { ...proseSx, ...sx };
  const clean = useMemo(() => (looksLikeHtml(value) ? sanitize(value) : null), [value]);
  const [pdf, setPdf] = useState(null);

  // Los PDF se abren en el visor de la aplicación en lugar de mandar al usuario a otra pestaña:
  // es el mismo trato que ya reciben los adjuntos de PQRS y las facturas. El resto de archivos
  // sigue su camino normal (el <a> ya lleva target y rel).
  //
  // Delegación en el contenedor porque el HTML entra por dangerouslySetInnerHTML y no hay
  // componentes React a los que colgar un onClick.
  const handleClick = (event) => {
    const link = event.target.closest?.('a.otx-file');
    if (!link) return;

    const href = link.getAttribute('href') || '';
    if (!isPdfUrl(href)) return;

    event.preventDefault();
    setPdf({ src: href, filename: link.textContent?.trim() || 'documento.pdf' });
  };

  if (clean !== null) {
    return (
      <>
        {/* eslint-disable-next-line react/no-danger */}
        <Box sx={mergedSx} onClick={handleClick} dangerouslySetInnerHTML={{ __html: clean }} />
        <PdfViewModal
          open={Boolean(pdf)}
          src={pdf?.src || ''}
          title={pdf?.filename || ''}
          filename={pdf?.filename || ''}
          onClose={() => setPdf(null)}
        />
      </>
    );
  }

  return (
    <Box sx={mergedSx}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{value || ''}</ReactMarkdown>
    </Box>
  );
}

ContentRenderer.propTypes = {
  value: PropTypes.string,
  sx: PropTypes.object
};

export default ContentRenderer;
